




























































































import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import Item from './Item.vue';
import SelectedItem from './SelectedItem.vue';
import BookMeetingSearch from './Search.vue';
import NewMeetingBookingFlow from './NewMeetingBookingFlow.vue';
import { Retailer, AutocompleteResult } from '@/models/BookMeetingFlow';
import { websiteText } from '@/mixins/websiteTexts';
import { logMeetingSolution, meetingTable } from '@/services/MeetingBookingLog';

@Component({ components: { Item, SelectedItem, Search: BookMeetingSearch, NewMeetingBookingFlow } })
class MeetingRetailerModal extends Vue {
    @Prop() sortedRetailers!: Retailer[];
    @Prop() descriptionTitle!: string;
    @Prop({ type: Boolean, default: false }) useNewMeetingBooking!: boolean;
    @Prop({ type: Boolean, default: false }) blockOnPage!: boolean;
    @Prop() currentCulture!: string;
    @Prop({ type: String, default: '' }) bookingSource!: string;
    selectedRetailerIndex: number = -1;
    selectedMeetingType: string | null = null;
    selectedOrganizationId: string | null = null;

    // This state is used to determine if we should autoplay the video or not when the component unmounts.
    private wasVideoPlaying: boolean = false;
    public websiteText = websiteText;
    public isClosestBrandStoreIndex: number = -1;  

    mounted() {
        const videoElement = this.getVideoElement();
        if (videoElement) {
            this.wasVideoPlaying = !videoElement.paused;
            videoElement.pause();
        }
        document.addEventListener('keydown', this.handleFocusTrap);

        meetingTable('MeetingRetailerModal', 'retailers in the list (block flag: ' + this.useNewMeetingBooking + ')',
            (this.sortedRetailers || []).map(r => ({
                name: r.name,
                organizationSystemId: r.organizationSystemId,
                useNewMeetingBooking: r.useNewMeetingBooking,
                availableMeetingTypes: (r.availableMeetingTypes || []).join(', '),
                oldUrls: [r.bookMeetingUrlStore, r.bookMeetingUrlHome, r.bookMeetingUrlVideo]
                    .filter(u => !!u).length,
                distanceKm: r.distance !== undefined && r.distance !== null ? Math.round(r.distance) : null
            })));

        this.$nextTick(() => {
            this.focusFirstElement();
        });
    }

    destroyed() {
        document.removeEventListener('keydown', this.handleFocusTrap);

        if (this.wasVideoPlaying) {
            const videoElement = this.getVideoElement();
            if (videoElement) {
                videoElement.play().catch(err => {
                    console.warn('Video playback was interrupted or blocked:', err);
                });
            }
        }
    }

    /***
     * Commented out for now, 
     * got feedback that the closest store should not be prioritized in the list for now,
     * but might be added back later.
     ***/ 
    // @Watch('sortedRetailers', { immediate: true })
    // @Watch('sortedRetailers', { immediate: true })
    // onSortedRetailersChange() {
    //     if (!this.sortedRetailers || this.sortedRetailers.length === 0) {
    //         this.isClosestBrandStoreIndex = -1;
    //         return;
    //     }

    //     const foundIndex = this.sortedRetailers.findIndex(retailer => retailer.organizationType === "ConceptStore");
        
    //     if (foundIndex > 0) {
    //         const [conceptStore] = this.sortedRetailers.splice(foundIndex, 1);
    //         this.sortedRetailers.unshift(conceptStore);
    //     }
        
    //     if (foundIndex !== -1) {
    //         this.isClosestBrandStoreIndex = 0; 
    //     } else {
    //         this.isClosestBrandStoreIndex = -1;
    //     }
    // }

    @Watch('selectedRetailerIndex')
    onRetailerChange() {
        this.selectedMeetingType = null;

        const retailer = this.selectedRetailer;
        if (retailer) {
            logMeetingSolution('MeetingRetailerModal', {
                useNewMeetingBooking: this.effectiveUseNewMeetingBooking,
                source: 'retailer.useNewMeetingBooking (' + retailer.useNewMeetingBooking +
                    ') || block prop (' + this.useNewMeetingBooking + ')',
                retailerName: retailer.name,
                organizationSystemId: retailer.organizationSystemId,
                availableMeetingTypes: retailer.availableMeetingTypes
            });
        }

        this.$nextTick(() => {
            this.focusFirstElement();
            this.scrollInnerContentToTop();
        });
    }

    public onMeetingTypeSelected(type: string) {
        this.selectedMeetingType = type;
        this.$nextTick(() => {
            this.scrollInnerContentToTop();
        });
    }

    private handleFocusTrap(e: KeyboardEvent) {
        if (e.key !== 'Tab') return;

        const modalElement = this.$refs.modalContent as HTMLElement;
        if (!modalElement) return;

        const focusableElements = modalElement.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>;

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === firstElement) {
                lastElement.focus();
                e.preventDefault();
            }
        } else {
            if (document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
            }
        }
    }

    private focusFirstElement() {
        const modalElement = this.$refs.modalContent as HTMLElement;
        if (!modalElement) return;

        const firstFocusable = modalElement.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;

        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    private scrollInnerContentToTop() {
        const root = this.$el as HTMLElement | null;
        if (!root) return;

        const scrollEl = root.querySelector('.modal-inner-scroll-content') as HTMLElement | null;
        if (!scrollEl) return;

        try {
            scrollEl.scrollTo({ top: 0, behavior: 'auto' });
        } catch (err) {
            scrollEl.scrollTop = 0;
        }
    }

    private getVideoElement(): HTMLVideoElement | null {
        const videoWrapper = document.getElementById('responsive-videos');
        return videoWrapper ? videoWrapper.querySelector('video') : null;
    }

    get selectedRetailer() {
        return this.sortedRetailers[this.selectedRetailerIndex];
    }

    /** Per-retailer channel setting takes priority; falls back to the block-level prop. */
    get effectiveUseNewMeetingBooking(): boolean {
        if (this.selectedRetailerIndex === -1) return this.useNewMeetingBooking;
        const retailer = this.selectedRetailer;
        return (retailer && retailer.useNewMeetingBooking) || this.useNewMeetingBooking;
    }

    @Emit('location-changed')
    public emitLocationChanged(location: { lat: number; lng: number }) {
        return location;
    }

    @Emit('item-selected')
    public emitItemSelected(result: AutocompleteResult) {
        return result;
    }

    public onItemSelected(payload: { index: number; organizationId?: string }) {
        if (payload && typeof payload.index === 'number') {
            this.selectedRetailerIndex = payload.index;
        }
        this.selectedOrganizationId = payload && payload.organizationId ? payload.organizationId : null;
    }
}

export default MeetingRetailerModal;
